import React, { useState } from 'react';
import { X, Send, Loader2, CheckCircle2, MessageCircle } from 'lucide-react';

/**
 * EnquireNowButton + popup.
 * Premium light modal, matches Book a Demo Slot aesthetic.
 * Drop anywhere: <EnquireNowButton courseName="Film Making" />
 */
const EnquireNowButton = ({
  courseName = 'Our Courses',
  className = '',
  variant = 'outline', // 'outline' (default — pairs with primary Enroll Now) | 'solid'
  fullWidth = false,
  label = 'Enquire Now',
  size = 'md'
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', course: courseName, message: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError('Please fill in name, phone and email.');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          course: form.course,
          message: form.message ? `Enquiry: ${form.message}` : 'Quick enquiry from popup',
          source: `enquire-now-${courseName}`
        })
      });
      if (!res.ok) throw new Error(`Lead capture failed (${res.status})`);
      if (window.gtag) window.gtag('event', 'enquire_submitted', { course: form.course });
      if (window.fbq) window.fbq('track', 'Lead', { content_name: form.course, content_category: 'enquiry' });
      setStatus('ok');
    } catch (_) {
      setStatus('error');
      setError('Something went wrong. Please WhatsApp us instead.');
    }
  };

  const baseBtn = 'inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all';
  const sizing = size === 'sm' ? 'px-5 py-2.5 text-sm' : 'px-6 py-3.5 text-base';
  const variantCls = variant === 'solid'
    ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-md'
    : 'bg-white text-slate-900 border-2 border-slate-300 hover:border-slate-900';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`enquire-now-${courseName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        className={`${baseBtn} ${sizing} ${variantCls} ${fullWidth ? 'w-full' : ''} ${className}`}
      >
        <MessageCircle className="w-4 h-4" /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="enquire-popup"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="enquire-popup-close"
              aria-label="Close enquiry form"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>

            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-7 py-6 text-white">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-300 mb-1">Quick Enquiry</p>
              <h3 className="text-2xl font-black leading-tight">Have a Question?</h3>
              <p className="text-sm text-slate-300 mt-1">Our counselor will get back within 24 hours.</p>
            </div>

            {status === 'ok' ? (
              <div className="p-8 text-center" data-testid="enquire-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h4 className="text-xl font-black text-slate-900 mb-2">Enquiry Received</h4>
                <p className="text-sm text-slate-600 mb-5">Thank you, <strong>{form.name}</strong>. A counselor will reach out shortly.</p>
                <a
                  href={`https://wa.me/919891550666?text=${encodeURIComponent('Hi, I just sent an enquiry about ' + form.course)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-5 py-3 rounded-xl"
                >
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp Now
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-7 space-y-3" data-testid="enquire-form">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" data-testid="enquire-name" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:bg-white" />
                <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" data-testid="enquire-phone" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:bg-white" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="enquire-email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:bg-white" />
                <select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} data-testid="enquire-course" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-slate-900 focus:bg-white">
                  {[courseName, 'AI Bootcamp', 'Performance Marketing Course', 'Film Making & Cinematic Animation', 'Game Development Course', 'Jewellery Designing in Blender', 'Blender & Character Design', 'Not Sure Yet'].filter((v, i, a) => a.indexOf(v) === i).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your question (optional)" data-testid="enquire-message" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:bg-white resize-none" />

                {error && <p className="text-red-600 text-sm">{error}</p>}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="enquire-submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-black py-4 rounded-xl text-sm shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Enquiry</>}
                </button>
                <p className="text-[10px] text-slate-400 text-center pt-1">Your details are 100% private. We will never spam you.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EnquireNowButton;
